import React, { useEffect, useMemo, useState } from "react";
import Card from "./Card";
import { mod } from "./mod";

export default function CardCarousel({
  datas = [],
  cardClickedIndex,
  onCardClick,
  isMobile = false,
  style = {},
}) {
  const N = datas.length;
  const CARD_ASPECT = isMobile ? 1.438 : 1.463;             // ≈ 1.425
  const GAP = 20;
  const cardWidth = isMobile ? 350 : 400; // Key: responsive card width!

  const calculatedHeight = Math.round(cardWidth * CARD_ASPECT);
  const finalCardHeight = calculatedHeight; // Use provided height or calculated
  const visibleCount = isMobile ? 1 : 5;


  // Virtual carousel state
  const [centerIndex, setCenterIndex] = useState(cardClickedIndex ?? 0);
  const [offset, setOffset] = useState(0); // Animation offset in card positions
  const [withTransition, setWithTransition] = useState(false);

  // Generate only visible cards dynamically (no copies!)
  const visibleCards = useMemo(() => {
    if (N === 0) return [];

    const halfVisible = Math.ceil(visibleCount / 2);
    const cards = [];

    // Render visible range + 1 buffer on each side
    for (let i = -halfVisible - 1; i <= halfVisible + 1; i++) {
      const dataIndex = mod(centerIndex + i, N);
      cards.push({
        data: datas[dataIndex],
        dataIndex: dataIndex,
        relativePosition: i, // Position relative to center
        key: `${dataIndex}-${i}`, // Unique key for React
      });
    }

    return cards;
  }, [centerIndex, N, visibleCount, datas]);

  // Handle external cardClickedIndex changes (from DotCarousel, etc.)
  useEffect(() => {
    if (typeof cardClickedIndex === "number" && N > 0 && cardClickedIndex !== centerIndex) {
      const distance = calculateShortestDistance(centerIndex, cardClickedIndex, N);

      setWithTransition(true);
      setOffset(-distance);

      setTimeout(() => {
        setWithTransition(false);
        setCenterIndex(cardClickedIndex);
        setOffset(0);
      }, 360);
    }
  }, [cardClickedIndex, centerIndex, N]);


  // Handle card clicks
  const onCardClickInternal = (relativePosition, dataIndex) => {
    if (relativePosition === 0) return; // Already centered

    setWithTransition(true);
    setOffset(-relativePosition);

    setTimeout(() => {
      setWithTransition(false);
      setCenterIndex(dataIndex);
      setOffset(0);
      onCardClick?.(dataIndex);
    }, 360);
  };

  // Calculate shortest wrap-around distance
  function calculateShortestDistance(from, to, total) {
    let distance = to - from;

    // Normalize to shortest path
    if (distance > total / 2) distance -= total;
    if (distance < -total / 2) distance += total;

    // Special cases for sequential navigation
    if (from === total - 1 && to === 0) distance = 1;
    if (from === 0 && to === total - 1) distance = -1;

    return distance;
  }
  if (N === 0) {
    return (
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          width: "100%",
          height: 0,
        }}
      />
    );
  }

  const viewportWidth = visibleCount * (cardWidth + GAP) - GAP;
  const top = isMobile ? 90 : 125;
  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        width: `${viewportWidth}px`,
        minWidth: `${viewportWidth}px`, // Force minimum width
        height: `${finalCardHeight}px`,
        margin: "0 auto",
        background: "transparent",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        top: top,
        ...style,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: `${GAP}px`,
          height: "100%",
          transform: `translate3d(${offset * (cardWidth + GAP)}px, 0, 0)`,
          transition: withTransition
            ? "transform 360ms cubic-bezier(.2,.8,.2,1)"
            : "none",
          willChange: withTransition ? "transform" : "auto",
          alignItems: "stretch",
          position: "relative",
          backfaceVisibility: "hidden",
          perspective: 1000,
          WebkitFontSmoothing: "antialiased",
        }}
      >
        {visibleCards.map((card) => (
          <div
            key={card.key}
            style={{
              width: `${cardWidth}px`,
              height: "100%",
              flex: "0 0 auto",
              position: "relative",
            }}
          >
            <Card
              website={card.data.website}
              image={card.data.image}
              title={card.data.title}
              date={card.data.date}
              contentText={card.data.contentText}
              hasBeenClicked={card.relativePosition === 0}
              onClick={() => onCardClickInternal(card.relativePosition, card.dataIndex)}
              isMobile={isMobile}
            />
          </div>

        ))}
      </div>
    </div>
  );
}